import router from './router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import { Route } from 'vue-router'
import { useUserStore } from './stores/user'
import { usePermissionStore } from './stores/permission'
import { useDeveloperModeStore } from './stores/developerMode'
import i18n from '@/lang' // Internationalization
import settings from './settings'
import {
  consumePostLoginRedirect,
  getRedirectPath,
  parseRedirectTarget
} from '@/utils/redirect'
import { isDeveloperOnlyRoute } from '@/utils/developer-mode'

NProgress.configure({ showSpinner: false })

const PUBLIC_ROUTES = ['/login', '/auth-redirect', '/reset-password', '/register']

const isPublicRoute = (path: string): boolean => PUBLIC_ROUTES.includes(path)

const getPageTitle = (key: string) => {
  const hasKey = i18n.te(`route.${key}`)
  if (hasKey) {
    const pageName = i18n.t(`route.${key}`)
    return `${pageName} - ${settings.title}`
  }
  return `${settings.title}`
}

router.beforeEach(async (to: Route, _: Route, next: any) => {
  const userStore = useUserStore()
  const permissionStore = usePermissionStore()

  NProgress.start()
  let redirectTarget: string | undefined
  let replace = false

  const authorizationCode = to.query.code

  if (authorizationCode) {
    await userStore.signinRedirectCallback()
    redirectTarget = consumePostLoginRedirect() || '/'
    replace = true
  }

  await userStore.getUser()

  if (to.path === '/register') {
    next()
    return
  }

  if (to.path === '/login' && typeof to.query.redirect === 'string') {
    const registerRedirect = to.query.redirect
    if (getRedirectPath(registerRedirect) === '/register') {
      const { path, query } = parseRedirectTarget(registerRedirect)
      next({ path, query, replace: true })
      return
    }
  }

  if (!userStore.user && isPublicRoute(to.path)) {
    next()
    return
  }

  if (userStore.user) {
    if (to.path === '/login') {
      redirectTarget = (to.query.redirect as string) || redirectTarget || '/'
    } else if (!permissionStore.routesGenerated) {
      await userStore.getManageable()
      const roles = userStore.roles

      permissionStore.generateRoutes(roles)
      permissionStore.dynamicRoutes.forEach(r => router.addRoute(r))

      if (redirectTarget) {
        const { path, query } = parseRedirectTarget(redirectTarget)
        next({ path, query, replace: true })
        return
      }

      next({ ...to, replace: true })
      return
    }
  } else {
    const targetPath = getRedirectPath((redirectTarget || to.fullPath).toString())
    if (!isPublicRoute(targetPath)) {
      redirectTarget = `/login?redirect=${encodeURIComponent(to.fullPath)}`
    }
  }

  if (redirectTarget) {
    const { path, query } = parseRedirectTarget(redirectTarget)
    next({ path, query, replace })
    return
  }

  const developerModeStore = useDeveloperModeStore()
  if (userStore.user && isDeveloperOnlyRoute(to) && !developerModeStore.enabled) {
    next({ path: '/', replace: true })
    return
  }

  next()
})

router.afterEach((to: Route) => {
  NProgress.done()
  document.title = getPageTitle(to?.meta?.title)
})
